import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Edit, Trash2, FolderKanban, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProjectDetail() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadProject();
  }, [projectId]);

  const loadProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`, {
        withCredentials: true
      });
      setProject(response.data);
      
      if (response.data.progress_percentage === 100 && response.data.status === 'completed') {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      }
    } catch (error) {
      console.error('Failed to load project:', error);
      toast.error('Failed to load project');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/projects/${projectId}`, {
        withCredentials: true
      });
      toast.success('Project deleted successfully');
      navigate('/projects');
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-4 text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          data-testid="back-button"
          variant="ghost"
          onClick={() => navigate('/projects')}
          className="mb-6 rounded-lg text-stone-600 hover:text-primary hover:bg-rose-50 font-medium"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Button>

        <div className="rounded-3xl bg-white border border-stone-100 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="bg-gradient-to-br from-rose-100 to-orange-100 p-12 text-center">
            <FolderKanban className="h-20 w-20 text-primary mx-auto mb-4" />
          </div>

          <div className="p-8">
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <h1 className="text-4xl font-heading font-extrabold tracking-tight mb-4">
                  {project.name}
                </h1>
                <div className="flex items-center space-x-2 mb-4">
                  {project.status === 'in_progress' ? (
                    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-accent-soft text-accent">
                      <Clock className="h-4 w-4 mr-2" />
                      In Progress
                    </span>
                  ) : project.status === 'completed' ? (
                    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-emerald-100 text-emerald-700">
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Completed
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-stone-100 text-stone-700">
                      Paused
                    </span>
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                <Button
                  data-testid="edit-button"
                  onClick={() => navigate(`/projects/${projectId}/edit`)}
                  className="rounded-full bg-white text-primary border-2 border-primary/20 hover:border-primary/50 hover:bg-rose-50 transition-all duration-300 font-bold"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  data-testid="delete-button"
                  onClick={() => setShowDeleteDialog(true)}
                  variant="ghost"
                  className="rounded-full text-destructive hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>

            {project.description && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Description</h3>
                <p className="text-base text-foreground font-body leading-relaxed">
                  {project.description}
                </p>
              </div>
            )}

            <div className="mb-6">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-medium text-muted-foreground">Progress</h3>
                <span className="text-2xl font-heading font-bold text-primary">{project.progress_percentage}%</span>
              </div>
              <div className="w-full h-4 bg-stone-100 rounded-full overflow-hidden">
                <div
                  data-testid="progress-bar"
                  className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-300"
                  style={{ width: `${project.progress_percentage}%` }}
                />
              </div>
            </div>

            {project.instructions && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Instructions / Notes</h3>
                <div className="rounded-2xl bg-stone-50 p-6">
                  <p className="text-base text-foreground font-body leading-relaxed whitespace-pre-wrap">
                    {project.instructions}
                  </p>
                </div>
              </div>
            )}

            <div className="border-t border-stone-100 pt-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Yarns Used</h3>
              {project.yarns_used && project.yarns_used.length > 0 ? (
                <p className="text-sm text-muted-foreground">
                  {project.yarns_used.length} {project.yarns_used.length === 1 ? 'yarn' : 'yarns'} linked to this project
                </p>
              ) : (
                <p className="text-sm text-muted-foreground font-accent">No yarns linked yet</p>
              )}
            </div>
          </div>
        </div>
      </main>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this project? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-button"
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
